<script setup lang="ts">
import { type ClassValue } from "clsx";
import { cva, type VariantProps } from "class-variance-authority";
import { Primitive, type PrimitiveProps } from "reka-ui";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/90",
                destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
                outlineSoft: "border border-gray-200 bg-white text-gray-800 shadow-2xs hover:bg-gray-50",
                outlineNeutral: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                primaryBlue: "bg-[#0066CC] text-white hover:bg-[#0055AA]",
                success: "bg-green-600 text-white hover:bg-green-700",
                link: "text-primary underline-offset-4 hover:underline"
            },
            size: {
                default: "h-10 px-4 py-2",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
                icon: "h-10 w-10"
            }
        },
        defaultVariants: {
            variant: "default",
            size: "default"
        }
    }
);

type ButtonVariants = VariantProps<typeof buttonVariants>;

interface Props extends PrimitiveProps {
    variant?: ButtonVariants["variant"];
    size?: ButtonVariants["size"];
    class?: ClassValue;
}

const props = withDefaults(defineProps<Props>(), {
    as: "button"
});
</script>

<template>
    <Primitive
        :as="props.as"
        :as-child="props.asChild"
        :class="cn(buttonVariants({ variant: props.variant, size: props.size }), props.class)"
    >
        <slot />
    </Primitive>
</template>
